import React, { useState } from 'react';
import axios from 'axios';

export default function ParticipantForm({ eventId, onCreated, initialData, onCancel }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    age: '',
    sex: '',
    email: '',
    phone: '',
    facebook: '',
    instagram: '',
    EventId: eventId || ''
  });

  const [loading, setLoading] = useState(false);

  // Sync form state when initialData changes (e.g. when clicking Edit in the table)
  React.useEffect(() => {
    if (initialData) {
      setForm({
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        age: initialData.age || '',
        sex: initialData.sex || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        facebook: initialData.facebook || '',
        instagram: initialData.instagram || '',
        EventId: eventId || initialData.EventId || ''
      });
    } else {
      setForm({
        firstName: '',
        lastName: '',
        age: '',
        sex: '',
        email: '',
        phone: '',
        facebook: '',
        instagram: '',
        EventId: eventId || ''
      });
    }
  }, [initialData, eventId]);

  const submit = async (e) => {
    e.preventDefault();
    const finalEventId = eventId || form.EventId;
    if (!finalEventId) {
      window.Swal.fire('Attenzione', "ID evento mancante", 'warning');
      return;
    }
    if (!form.sex) {
      window.Swal.fire('Attenzione', "Seleziona il sesso del partecipante", 'warning');
      return;
    }
    try {
      setLoading(true);
      if (initialData && initialData.id) {
        await axios.put(`/speeddate/api/participants/${initialData.id}`, { ...form, EventId: finalEventId });
        window.Swal.fire({
          title: 'Aggiornato!',
          text: `${form.firstName} è stato aggiornato.`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          customClass: { popup: 'swal2-glass' }
        });
      } else {
        await axios.post('/speeddate/api/participants', { ...form, EventId: finalEventId });
        window.Swal.fire({
          title: 'Registrato!',
          text: `${form.firstName} è stato aggiunto alla lista.`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          customClass: { popup: 'swal2-glass' }
        });
        setForm({
          firstName: '',
          lastName: '',
          age: '',
          sex: '',
          email: '',
          phone: '',
          facebook: '',
          instagram: '',
          EventId: eventId || ''
        });
      }
      if (onCreated) onCreated();
    } catch (err) {
      console.error('ParticipantForm error:', err.response?.data || err.message || err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || "Non è stato possibile completare l'operazione.";
      window.Swal.fire({
        title: 'Errore',
        text: errorMessage,
        icon: 'error',
        customClass: { popup: 'swal2-glass' }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="mb-3">
        <label className="form-label small fw-bold text-muted">Nome & Cognome</label>
        <div className="row g-2">
          <div className="col-6">
            <input
              className="form-control form-control-sm shadow-none"
              placeholder="Nome"
              required
              value={form.firstName}
              onChange={e => setForm({ ...form, firstName: e.target.value })}
            />
          </div>
          <div className="col-6">
            <input
              className="form-control form-control-sm shadow-none"
              placeholder="Cognome"
              required
              value={form.lastName}
              onChange={e => setForm({ ...form, lastName: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-4">
          <label className="form-label small fw-bold text-muted">Età</label>
          <input
            type="number"
            className="form-control form-control-sm shadow-none"
            placeholder="00"
            required
            min="0"
            value={form.age}
            onChange={e => setForm({ ...form, age: e.target.value })}
          />
        </div>
        <div className="col-8">
          <label className="form-label small fw-bold text-muted">Sesso</label>
          <div className="d-flex gap-2">
            <div className="flex-fill">
              <input
                type="radio"
                className="btn-check"
                name="sex"
                id="sex-male"
                value="male"
                checked={form.sex === 'male'}
                onChange={e => setForm({ ...form, sex: e.target.value })}
              />
              <label
                className={`btn btn-sm w-100 py-2 px-0 shadow-none border-2 d-flex flex-column align-items-center justify-content-center transition-all ${form.sex === 'male' ? 'btn-primary border-primary shadow-lg scale-105' : 'btn-outline-primary border-primary border-opacity-25 opacity-50'}`}
                htmlFor="sex-male"
                style={{ borderRadius: '12px', minHeight: '60px' }}
              >
                <i className={`fas ${form.sex === 'male' ? 'fa-check-circle' : 'fa-mars'} mb-1 fa-lg`}></i>
                <span className="fw-bold" style={{ fontSize: '11px' }}>Uomo</span>
              </label>
            </div>
            <div className="flex-fill">
              <input
                type="radio"
                className="btn-check"
                name="sex"
                id="sex-female"
                value="female"
                checked={form.sex === 'female'}
                onChange={e => setForm({ ...form, sex: e.target.value })}
              />
              <label
                className={`btn btn-sm w-100 py-2 px-0 shadow-none border-2 d-flex flex-column align-items-center justify-content-center transition-all ${form.sex === 'female' ? 'btn-danger border-danger shadow-lg scale-105' : 'btn-outline-danger border-danger border-opacity-25 opacity-50'}`}
                htmlFor="sex-female"
                style={{ borderRadius: '12px', minHeight: '60px' }}
              >
                <i className={`fas ${form.sex === 'female' ? 'fa-check-circle' : 'fa-venus'} mb-1 fa-lg`}></i>
                <span className="fw-bold" style={{ fontSize: '11px' }}>Donna</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label small fw-bold text-muted">Indirizzo Email *</label>
        <input
          type="email"
          className="form-control form-control-sm shadow-none"
          placeholder="email@example.com"
          required
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label small fw-bold text-muted">Telefono (Opzionale)</label>
        <input
          type="text"
          className="form-control form-control-sm shadow-none"
          placeholder="+39 3XX XXXX"
          value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
        />
      </div>

      <div className="row g-2 mb-4">
        <div className="col-6">
          <label className="form-label small fw-bold text-muted">Facebook</label>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0"><i className="fab fa-facebook-f text-primary"></i></span>
            <input
              type="text"
              className="form-control shadow-none border-start-0"
              placeholder="Username"
              value={form.facebook}
              onChange={e => setForm({ ...form, facebook: e.target.value })}
            />
          </div>
        </div>
        <div className="col-6">
          <label className="form-label small fw-bold text-muted">Instagram</label>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0"><i className="fab fa-instagram text-danger"></i></span>
            <input
              type="text"
              className="form-control shadow-none border-start-0"
              placeholder="@username"
              value={form.instagram}
              onChange={e => setForm({ ...form, instagram: e.target.value })}
            />
          </div>
        </div>
      </div>

      <div className="d-grid gap-2 mt-2">
        <button type="submit" className={`btn ${initialData ? 'btn-success' : 'btn-primary'} shadow-sm`} disabled={loading}>
          {loading ? 'Attendere...' : (initialData ? 'Aggiorna Partecipante' : 'Registra Partecipante')}
        </button>
        {initialData && (
          <button type="button" className="btn btn-outline-secondary shadow-none border-0 mt-1" onClick={onCancel}>
            <i className="fas fa-times me-2"></i>Annulla Modifica
          </button>
        )}
      </div>
    </form>
  );
}
